import { BookOpen, FileText, Loader2 } from 'lucide-react';
import { useState } from 'react';

import {
  useArtigosDaDiscussaoControllerDetalhar,
  useArtigosDaDiscussaoControllerListar,
} from '@/api/generated/discussões-técnicas/discussões-técnicas';
import { VerPdfOriginal } from '@/features/discussoes/components/VerPdfOriginal';
import { TextoMarkdown } from '@/shared/components/TextoMarkdown';
import { Card, CardContent } from '@/shared/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/shared/ui/dialog';

/**
 * O título já está no cabeçalho do pop-up: um `# título` igual no começo do
 * texto (o PDF importado quase sempre traz) apareceria duas vezes seguidas.
 */
function semTituloRepetido(markdown: string, titulo: string): string {
  const [primeira = '', ...resto] = markdown.trimStart().split('\n');
  const cabecalho = /^#{1,6}\s+(.*)$/.exec(primeira.trim());
  const normalizar = (s: string) => s.replace(/[*_`]/g, '').trim().toLocaleLowerCase('pt-BR');
  return cabecalho && normalizar(cabecalho[1]!) === normalizar(titulo) ? resto.join('\n').trimStart() : markdown;
}

interface Props {
  discussaoId: string;
  assessorNome: string | null;
}

/**
 * Rodapé da discussão: os artigos PUBLICADOS do advisor desta discussão.
 *
 * Sem polling, ao contrário do detalhe da discussão (2 s): artigo muda raramente
 * e a lista só precisa estar certa quando a tela abre.
 *
 * Não renderiza nada quando a lista vem vazia — advisor sem artigo não deixa
 * um quadro vazio no fim da tela.
 */
export function ArtigosDoAdvisor({ discussaoId, assessorNome }: Props) {
  const { data } = useArtigosDaDiscussaoControllerListar(discussaoId);
  const [abertoId, setAbertoId] = useState<string | null>(null);

  if (!data || data.itens.length === 0) return null;

  return (
    <section className="bg-surface-raised rounded-lg border-2 border-brand/20 p-4 space-y-3">
      <h3 className="font-semibold text-foreground flex items-center gap-2">
        <BookOpen className="w-5 h-5 text-brand" />
        {assessorNome ? `Artigos de ${assessorNome}` : 'Artigos do especialista'}
      </h3>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {data.itens.map((artigo) => (
          <Card
            key={artigo.id}
            role="button"
            tabIndex={0}
            onClick={() => setAbertoId(artigo.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setAbertoId(artigo.id);
              }
            }}
            className="cursor-pointer border-border transition-colors hover:border-brand/50 hover:bg-brand/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand/40"
          >
            <CardContent className="p-4">
              <div className="flex items-start gap-2">
                <FileText className="w-4 h-4 text-brand mt-0.5 shrink-0" />
                <h4 className="font-medium text-foreground line-clamp-2">{artigo.titulo}</h4>
              </div>
              <p className="text-sm text-muted-foreground mt-2 line-clamp-3">{artigo.trecho}</p>
              <p className="text-xs text-brand mt-3">Ler artigo</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <ArtigoDialogo
        discussaoId={discussaoId}
        artigoId={abertoId}
        aoFechar={() => setAbertoId(null)}
      />
    </section>
  );
}

function ArtigoDialogo({
  discussaoId,
  artigoId,
  aoFechar,
}: {
  discussaoId: string;
  artigoId: string | null;
  aoFechar: () => void;
}) {
  const { data: artigo, isPending, isError } = useArtigosDaDiscussaoControllerDetalhar(
    discussaoId,
    artigoId ?? '',
    { query: { enabled: !!artigoId } },
  );
  return (
    <Dialog open={!!artigoId} onOpenChange={(v) => !v && aoFechar()}>
      <DialogContent className="max-w-3xl max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="pr-6">{artigo?.titulo ?? 'Artigo'}</DialogTitle>
          <DialogDescription>
            {artigo ? `Atualizado em ${new Date(artigo.atualizadoEm).toLocaleDateString('pt-BR')}` : ' '}
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto pr-1">
          {isPending ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-brand" />
            </div>
          ) : isError ? (
            <p className="text-sm text-destructive py-6">Não foi possível abrir o artigo.</p>
          ) : (
            <TextoMarkdown texto={semTituloRepetido(artigo.conteudo, artigo.titulo)} />
          )}
        </div>

        {artigo?.temPdf && (
          <div className="flex justify-end border-t border-border pt-3">
            {/* key: o aviso de indisponível é de UM artigo, não pode vazar para o próximo aberto. */}
            <VerPdfOriginal key={artigo.id} url={artigo.pdfUrl} />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
